<template>
  <div class="top-picks">
    <div class="flex justify-between align-center">
      <div class="top-picks-title">{{$t('Featured properties')}}</div>
      <div class="slider-navigation">
        <div class="slider-navigation__btn" @click="scroll('left')">
          <span class="icon icon-chevron-left"></span>
        </div>
        <div class="slider-navigation__btn" @click="scroll('right')">
          <span class="icon icon-chevron-right"></span>
        </div>
      </div>
    </div>
    <div class="top-picks--list flex overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4" ref="scrollContainer" v-if="hotels.length > 0">
      <a
        v-for="hotel in hotels"
        :key="hotel.id"
        :href="`/hotels/results/${hotel.id}-${hotel.slug}`"
        class="top-picks--item"
      >
        <div class="top-picks--image">
          <img v-if="hotel.cover_image?.url" :src="imageUrlWithProxy(hotel.cover_image?.url, 357, 218)" :alt="hotel.name">
          <PhotoPlaceholder v-else :font-size="'42px'"/>
        </div>
        <div class="top-picks--content">
          <div class="top-picks--title">{{hotel.name}}</div>
          <div class="top-picks--location">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9 16.4248L9.59737 17.0072C9.51898 17.0842 9.42582 17.1452 9.32324 17.1869C9.22065 17.2286 9.11066 17.25 8.99958 17.25C8.8885 17.25 8.77851 17.2286 8.67592 17.1869C8.57334 17.1452 8.48017 17.0842 8.40178 17.0072L8.39672 17.0014L8.38237 16.9874L8.33006 16.9354C8.03263 16.6359 7.74009 16.3317 7.45256 16.023C6.73065 15.2496 6.03939 14.4493 5.38031 13.6239C4.62769 12.6752 3.85819 11.6027 3.27347 10.5434C2.70141 9.50477 2.25 8.37949 2.25 7.3499C2.25 3.60528 5.28075 0.75 9 0.75C12.7192 0.75 15.75 3.60528 15.75 7.3499C15.75 8.37949 15.2986 9.50477 14.7265 10.5426C14.1418 11.6035 13.3732 12.6752 12.6197 13.6239C11.6978 14.7786 10.7131 15.884 9.66994 16.9354L9.61763 16.9874L9.60328 17.0014L9.59822 17.0064L9 16.4248ZM9 9.82487C9.67133 9.82487 10.3152 9.56411 10.7899 9.09997C11.2646 8.63582 11.5312 8.00631 11.5312 7.3499C11.5312 6.6935 11.2646 6.06399 10.7899 5.59984C10.3152 5.13569 9.67133 4.87494 9 4.87494C8.32867 4.87494 7.68484 5.13569 7.21014 5.59984C6.73543 6.06399 6.46875 6.6935 6.46875 7.3499C6.46875 8.00631 6.73543 8.63582 7.21014 9.09997C7.68484 9.56411 8.32867 9.82487 9 9.82487Z" fill="#292929"/>
            </svg>
            <span>{{hotel.address}}</span>
          </div>
          <div class="top-picks--rate">
            <Rating :model-value="hotel.rating || undefined" disabled :cancel="false" readonly>
              <template #officon>
                <span/>
              </template>
            </Rating>
            {{hotel?.rating}}
          </div>
          <div class="top-picks--description" v-if="false">
            <div class="top-picks__amenities" v-if="hotel?.hotel_amenities">
              <div class="top-picks__amenity" v-for="amenity in hotelAmenities(hotel.hotel_amenities)">
                <div class="top-picks__amenity-icon">
                  <span class="icon" :class="`icon ${amenity.icon}`"/>
                </div>
                <div class="top-picks__amenity-text">{{amenity.name}}</div>
              </div>
            </div>
          </div>
          <div class="top-picks--price flex justify-between" v-if="(hotel as any)?.price_from">
            <div class="top-picks-rating">
              <template v-if="hotel.avg_rating && hotel.avg_rating > 0">
                <div :class="['top-picks-rating-score', ratingClass(hotel.avg_rating)]">
                  <div class="top-picks-rating-score-text">{{ round(hotel.avg_rating) }}</div>
                </div>
                <div class="top-picks-rating-text">
                  <div class="top-picks-rating-title">
                    <span v-if="Number(round(hotel.avg_rating)) >= 9">Excellent</span>
                    <span v-else-if="Number(round(hotel.avg_rating)) >= 7">Very Good</span>
                    <span v-else-if="Number(round(hotel.avg_rating)) >= 6">Good</span>
                    <span v-else-if="Number(round(hotel.avg_rating)) >= 4">Fair</span>
                    <span v-else-if="Number(round(hotel.avg_rating)) > 0">Poor</span>
                    <span v-else>{{$t('No reviews')}}</span>
                  </div>
                  <div class="top-picks-rating-reviews">{{ hotel.reviews_count || 0 }} {{$t('reviews')}}</div>
                </div>
              </template>
            </div>
            <div class="top-picks--price-from" >
              <div class="top-picks--hotel-price">{{ (hotel as any).price_from.converted.formatted }}</div>
              <div class="top-picks--hotel-taxes">includes taxes & fees</div>
            </div>
          </div>
        </div>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import Rating from 'primevue/rating'
import { imageUrlWithProxy } from '@/utils/imageproxy'
import dayjs from '~/utils/dayjs';
import { ref } from "vue";
import {TOP_AMENITIES} from "~/utils/hotels";
const api = useApi()
const scrollContainer = ref(null as any)
const startDate = dayjs(new Date()).format("YYYY-MM-DD");
const endDate = dayjs(startDate).add(2, 'day').format("YYYY-MM-DD");

const data = ref(null)

const hotels = ref([
  {
    "id": 7184811,
    "avg_rating": 0,
    "overall_rating": 0,
    "name": "Atlas Hotel | አትላስ ሆቴል",
    "slug": "atlas-hotel-",
    "description": "Located in the central Bole district of Addis Ababa, Atlas International Hotel is just 4 km from Bole International Airport and a short walk from Edna Mall.\nWhether you're in town for meetings or a quick getaway, the hotel puts you close to shops, cafés, and city highlights, without the downtown hassle. From here, it's easy to explore Medhane Alem Church, grab Ethiopian coffee at Tomoca (the locals' pick), or browse the stalls at Meskel Square.\nEach of the 64 rooms comes with a private balcony overlooking the skyline. Opt for a snug single if you're travelling solo, or stretch out in one of our family suites, complete with kitchenettes and full-size fridges for longer stays. Rooms feature proper air conditioning, fast Wi-Fi (50+ Mbps), and space to get some work done. A buffet breakfast is served each morning before you head out, featuring fresh fruit, eggs, local bread, and traditional Ethiopian coffee.\nThere's free self and valet parking, a casual restaurant and bar on-site that serves until 10pm, and someone at the front desk around the clock. We offer free airport pickup upon request.",
    "address": "atlas hotel, Addis Ababa, ET",
    "guarantee_type": null,
    "check_in_time": null,
    "check_out_time": null,
    "latitude": 9.0037327,
    "longitude": 38.7805824,
    "time_zone": "Africa/Nairobi",
    "rating": null,
    "hotel_type": null,
    "geo_place": null,
    "categories": null,
    "hotel_amenities": null,
    "room_amenities": null,
    "amenities": null,
    "issues": null,
    "chain": null,
    "child_policies": null,
    "catering_facilities": null,
    "business_facilities": null,
    "entertainment_facilities": null,
    "health_facilities": null,
    "sport_facilities": null,
    "keep_in_mind_info": null,
    "pet_policies": null,
    "location_info": null,
    "payment_method": null,
    "cover_image": {
      "url": "https://www.bookdirect.com/api/product-images/hotels/ed483d3e-c4ee-4e82-aa0f-86aeb1f1cf4e",
      "is_uploaded": false,
      "description": null
    },
    "images": {
      "Other": [
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/ed483d3e-c4ee-4e82-aa0f-86aeb1f1cf4e",
          "is_uploaded": false,
          "description": null
        }
      ]
    },
    "reviews_count": 0
  },
  {
    "id": 7184816,
    "avg_rating": 0,
    "overall_rating": 0,
    "name": "Oceans Edge Resort",
    "slug": "oceans-edge-resort",
    "description": "Oceans Edge Resort is a serene paradise located on the pristine shores of Carabao Island, Romblon, just 30 minutes by speedboat from the world-renowned Boracay Island. Nestled amidst lush greenery and turquoise waters, the resort offers a tranquil escape from the bustling crowds, providing guests with an intimate and rejuvenating experience.\nWith 42 elegantly designed villas spread across Premium Cliff, Ocean View, and Garden settings, the resort seamlessly blends luxury with nature. Guests can indulge in world-class amenities, including a stunning infinity pool overlooking Tablas Bay, a modern clubhouse, a spa and wellness retreat, and a vibrant dive shop catering to underwater enthusiasts.",
    "address": "Barangay Lanas, San Jose, 5510, PH",
    "guarantee_type": null,
    "check_in_time": "02:00 PM",
    "check_out_time": "12:30 PM",
    "latitude": 12.078227,
    "longitude": 121.9146118,
    "time_zone": "Asia/Manila",
    "rating": null,
    "hotel_type": null,
    "geo_place": null,
    "categories": null,
    "hotel_amenities": null,
    "room_amenities": null,
    "amenities": null,
    "issues": null,
    "chain": null,
    "child_policies": null,
    "catering_facilities": null,
    "business_facilities": null,
    "entertainment_facilities": null,
    "health_facilities": null,
    "sport_facilities": null,
    "keep_in_mind_info": null,
    "pet_policies": null,
    "location_info": null,
    "payment_method": null,
    "cover_image": {
      "url": "https://www.bookdirect.com/api/product-images/hotels/ec32556d-c7e3-435f-ac39-5a2b4e677548",
      "is_uploaded": false,
      "description": null
    },
    "images": {
      "Other": [
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/327c5b0b-ca0f-4cde-be2c-e41e33f81ad4",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/9e2901aa-0e8e-486a-9f02-feb18c896009",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/f0669a50-56d0-451a-8284-a35fdf5f4b37",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/2f5fa3bd-6230-44ca-9aa7-96dbec6bdc8a",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/b4e50f2e-553b-4f41-8b11-c76ec217b486",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/ab292360-77d6-4dbd-81f6-d4b81fffcbd1",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/80d21618-78f7-4211-a9c0-02aced4dadf6",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/a74c624b-a2ef-4b12-8925-83803f8f80b4",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/11e88fab-0a4c-4d2f-95b0-892226092bb0",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/1f597fc6-2f9a-409f-a9ad-945d035c9f0d",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/c2dcdc12-8daa-418d-851d-65fde2e78682",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/235c8cb6-7af4-49cb-8b16-8784aee73236",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/ec32556d-c7e3-435f-ac39-5a2b4e677548",
          "is_uploaded": false,
          "description": null
        }
      ]
    },
    "reviews_count": 0
  },
  {
    "id": 7184813,
    "avg_rating": 0,
    "overall_rating": 0,
    "name": "Amed Beach Resort",
    "slug": "amed-beach-resort",
    "description": "Perched on the quiet shores of Amed Beach in Karangasem, Amed Beach Resort is a short walk from local shops and dive centres, making it an inviting spot in Bali’s laid-back northeast. Maybe you’re here to just kick back on the beach or maybe you’re keen to explore the colourful marine life, our resort is perfect either way, with cafés, bars, and fishing boats nearby. From here, you can book a snorkelling trip, head to the famous Gates of Heaven at Lempuyang Temple, or hop on a boat at sunrise to catch Mount Agung waking up. All 11 rooms have a balcony or patio, and plenty come with sea views or open right onto the pool. Travelling solo? Grab a snug twin room. With the family? There’s room to spread out. You’ll find proper air conditioning, solid Wi-Fi, and daily cleaning to keep things feeling fresh. Breakfast’s served up each morning in the on-site Italian spot—coffee’s strong, seafood’s fresh. Parking’s free, the beachfront bar stays open late, and the staff are always around to help out—be it for a diving trip, a massage, or just a bit of local advice. ",
    "address": "Amed Beach, Jl. I Ketut Natih, Purwakerti, Kec. Abang, Kabupaten Karangasem, Bali 80852, Indonesia, Karangasem, 80852, ID",
    "guarantee_type": null,
    "check_in_time": null,
    "check_out_time": null,
    "latitude": -8.3354249,
    "longitude": 115.6576354,
    "time_zone": "Asia/Makassar",
    "rating": null,
    "hotel_type": null,
    "geo_place": null,
    "categories": null,
    "hotel_amenities": null,
    "room_amenities": null,
    "amenities": null,
    "issues": null,
    "chain": null,
    "child_policies": null,
    "catering_facilities": null,
    "business_facilities": null,
    "entertainment_facilities": null,
    "health_facilities": null,
    "sport_facilities": null,
    "keep_in_mind_info": null,
    "pet_policies": null,
    "location_info": null,
    "payment_method": null,
    "cover_image": {
      "url": "https://www.bookdirect.com/api/product-images/hotels/8051f8a1-f161-44fb-bd7c-56a1bda6106c",
      "is_uploaded": false,
      "description": null
    },
    "images": {
      "Other": [
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/8051f8a1-f161-44fb-bd7c-56a1bda6106c",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/4c6bda9b-f5bd-4d5f-a7aa-2ca0999ede83",
          "is_uploaded": false,
          "description": null
        }
      ]
    },
    "reviews_count": 0
  },
  {
    "id": 7184900,
    "avg_rating": 0,
    "overall_rating": 0,
    "name": "NOVUS LAURELES",
    "slug": "novus-laureles",
    "description": "Ubicación Estratégica: A solo 14 minutos a pie de la Plaza de Toros La Macarena y cerca del Estadio Atanasio Girardot, facilitando el acceso a eventos y reuniones.\nConectividad: wifi sin costo en todas las zonas y alojamientos dotados con escritorio y televisión de pantalla plana, perfectos para trabajar con confort.\nServicios Profesionales: Recepción 24 horas con personal multilingüe (español, inglés, francés e italiano) y servicio de traslado al aeropuerto disponible.",
    "address": "Cra 69 # 46B 34, Medellín, 050031, CO",
    "guarantee_type": null,
    "check_in_time": "03:00 AM",
    "check_out_time": "01:00 PM",
    "latitude": 6.2521243,
    "longitude": -75.5869833,
    "time_zone": "America/Bogota",
    "rating": null,
    "hotel_type": null,
    "geo_place": null,
    "categories": null,
    "hotel_amenities": null,
    "room_amenities": null,
    "amenities": null,
    "issues": null,
    "chain": null,
    "child_policies": null,
    "catering_facilities": null,
    "business_facilities": null,
    "entertainment_facilities": null,
    "health_facilities": null,
    "sport_facilities": null,
    "keep_in_mind_info": null,
    "pet_policies": null,
    "location_info": null,
    "payment_method": null,
    "cover_image": {
      "url": "https://www.bookdirect.com/api/product-images/hotels/d44b0274-008b-4756-9b7e-d01b49a1c69e",
      "is_uploaded": false,
      "description": null
    },
    "images": {
      "Other": [
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/d44b0274-008b-4756-9b7e-d01b49a1c69e",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/60b3bd13-03c9-4f1f-b62f-d9d803fc976b",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/939d7a67-fd18-4172-95aa-ee72119228aa",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/ab62947c-f882-4cb9-b6b7-bc523c95cf82",
          "is_uploaded": false,
          "description": null
        },
        {
          "url": "https://www.bookdirect.com/api/product-images/hotels/59840f41-04fd-49e5-948e-fdeb8dfc8c24",
          "is_uploaded": false,
          "description": null
        }
      ]
    },
    "reviews_count": 0
  }
])


// const { data: fetchedData } = await useAsyncData('get-hotels', fetchData)
// if (fetchedData.value) {
//   data.value = fetchedData.value
// }

const scroll = (direction:string) => {
  const container = scrollContainer.value
  const scrollAmount = 357 + 24
  container?.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
  })
}

function round(rating:number) {
  return  ((rating ^ 0) === rating) ? (+rating).toFixed() : (+rating).toFixed(1)
}

const ratingClass = (rate: any) => {
  const rating = round(rate)
  if (rating) {
    if (Number(rating) >= 9) {
      return 'excellent'
    } else if (Number(rating) >= 7) {
      return 'very-good'
    } else if (Number(rating) >=6) {
      return 'good'
    } else if (Number(rating) >= 4) {
      return 'fair'
    } else if (Number(rating) > 0) {
      return 'poor'
    }
  }
  return 'no-reviews'
}

const hotelAmenities = (amenities:any) => {
  const _hotelAmenities = amenities ? amenities : []
  return TOP_AMENITIES.filter((amenity: any) => {
    return _hotelAmenities.find((hotelAmenity: any) => Number(hotelAmenity.ota_code) === amenity.code)
  }).slice(0,3)
}

const place_id = 192;
const city_slug = 'us-fl-miami';

// async function fetchData() {
//   try {
//     let params = {
//       criteria: {
//         date_start: startDate,
//         date_end: endDate,
//         adults: 2
//       },
//       filters: {
//         geo_place_id: place_id
//       },
//       sort: "rating_desc"
//     }
//     return await api('api/product/v3/hotels/search-availability', {
//       method: 'POST',
//       body: params
//     })
//   } catch (error) {
//     console.log(error)
//     return null
//   }
// }
//
// onMounted(() => {
//   fetchData()
// })
</script>


<style lang="scss">
$color-white: var(--white);
$color-light-gray: var(--grey-border);
$color-green: #44B50C;
$color-dark-green: var(--green);
$color-dark-gray: var(--black);
$color-mid-gray: var(--dark-grey);
$color-yellow: var(--yellow);
$color-blue: var(--blue);

$tablet-border: 1024px;
$mobile-border: 767px;
.top-picks-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 36px;
  margin-bottom: 20px;
}
.top-picks--list {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.top-picks {
  &--item {
    min-width: 357px;
    width: 100%;
    border: 2px solid var(--grey-border);
    border-radius: 10px;

    &:hover {
      border: 2px solid var(--dark-grey);
    }
  }

  &--image {
    border-radius: 8px;
    margin: -2px;
    overflow: hidden;
    height: 215px;

    img {
      width: 100%;
    }
  }

  &--content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: var(--dark-grey);

  }

  &--title {
    color: var(--black);
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    text-overflow: ellipsis;
    max-height: 24px;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-break: break-word;
    overflow: hidden;
  }

  &--location {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;

    span  {
      max-height: 24px;
      text-overflow: ellipsis;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
      display: -webkit-box;
      overflow: hidden;
      word-break: break-word;
    }

    svg {
      min-width: 18px;
    }
  }

  &--description {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 72px;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    word-break: break-word;
    box-sizing: content-box
  }

  &--duration {
    font-size: 14px;
    display: flex;
    gap: 5px;
    align-items: center;
    width: fit-content;
    border: 1px solid var(--dark-grey);
    border-radius: 10px;
    padding: 4px 12px;
  }

  &--price {
    border-top: 2px solid var(--grey-border);
    padding-top: 12px;
    text-align: right;
    strong {
      color: var(--black);
    }
  }

  &-rating {
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    display: inline-flex;

    @media screen and (max-width: $mobile-border) {
      flex-direction: row-reverse;
    }

    &-text {
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      display: inline-flex;

      @media screen and (max-width: $mobile-border) {
        align-items: flex-start;
      }
    }

    &-title {
      color: $color-dark-gray;
      font-size: 14px;
      font-family: 'Albert Sans', sans-serif;
      font-weight: 500;
      line-height: 20px;
      word-wrap: break-word;
    }

    &-reviews {
      color: $color-mid-gray;
      font-size: 12px;
      font-family: 'Albert Sans', sans-serif;
      font-weight: 400;
      line-height: 16px;
      word-wrap: break-word;
    }

    &-score {
      height: 40px;
      width: 40px;
      padding: 8px 0;
      background: var(--black);
      border-radius: 4px;
      justify-content: center;
      align-items: center;
      gap: 4px;
      display: flex;

      &.excellent {
        background: var(--green);
      }

      &.very-good, &.good {
        background: var(--light-green);
      }

      &.poor, &.fair {
        background: var(--black);
      }

      &-text {
        flex: 1 1 0;
        text-align: center;
        color: $color-white;
        font-size: 16px;
        font-family: 'Albert Sans', sans-serif;
        font-weight: 600;
        line-height: 24px;
        word-wrap: break-word;
      }
    }

  }

  &--rate {
    display: flex;
    gap: 5px;
    align-items: center;
    font-size: 14px;
  }

  &__amenities {
    gap: 12px;
    display: flex;
    flex-wrap: wrap;

    @media screen and (max-width: $tablet-border) and (min-width: $mobile-border) {
      flex-direction: column;
      gap: 6px;
    }
  }

  &__amenity {
    align-items: center;
    gap: 16px;
    display: inline-flex;

    &-icon {
      justify-content: flex-start;
      align-items: center;
      gap: 8px;
      display: flex;
      font-size: 16px;
    }

    &-text {
      color: $color-mid-gray;
      font-size: 14px;
      font-family: 'Albert Sans', sans-serif;
      font-weight: 400;
      line-height: 20px;
      word-wrap: break-word;
    }
  }

  &--hotel-price {
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0;
    text-align: right;
    color: var(--black);
  }
  &--hotel-taxes {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    text-align: right;
  }
}

.slider-navigation {
  display: flex;
  justify-content: space-between;
  gap: 8px;

  &__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    width: 32px;
    height: 32px;
    background: var(--blue-highlight);
    color: var(--blue);
    border-radius: 10px;
    cursor: pointer;

    &:hover {
      background: var(--blue);
      color: #fff;
    }

    .disabled {
      opacity: 0.4;
    }
  }
}

/* Optional: hide scrollbar */
::-webkit-scrollbar {
  display: none;
}
</style>
